/**
 * RangeFilter Komponenti
 * 
 * Range slider filtreleri için genel komponent
 * Min/Max input'ları, range slider, currency formatting desteği
 */

'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'

export interface RangeFilterProps {
    fieldKey: string
    label: string
    min: number
    max: number
    step?: number
    value?: { min: number; max: number }
    onChange?: (value: { min: number; max: number }) => void
    formatValue?: (value: number) => string // Currency formatting için
    unit?: string // "day", "price", vb.
    className?: string
}

export default function RangeFilter({
    fieldKey,
    label,
    min,
    max,
    step = 1,
    value,
    onChange,
    formatValue,
    unit,
    className = '',
}: RangeFilterProps) {
    const { getVariable } = useLanguageVariables()
    const [localMin, setLocalMin] = useState(value?.min ?? min)
    const [localMax, setLocalMax] = useState(value?.max ?? max)
    const trackRef = useRef<HTMLDivElement>(null)

    // Value prop değiştiğinde local state'i güncelle
    useEffect(() => {
        if (value) {
            setLocalMin(value.min)
            setLocalMax(value.max)
        }
    }, [value])

    const handleMinChange = useCallback(
        (newMin: number) => {
            const clampedMin = Math.max(min, Math.min(newMin, localMax))
            setLocalMin(clampedMin)
            onChange?.({ min: clampedMin, max: localMax })
        },
        [min, localMax, onChange]
    )

    const handleMaxChange = useCallback(
        (newMax: number) => {
            const clampedMax = Math.min(max, Math.max(newMax, localMin))
            setLocalMax(clampedMax)
            onChange?.({ min: localMin, max: clampedMax })
        },
        [max, localMin, onChange]
    )

    const formatDisplayValue = useCallback(
        (val: number) => {
            if (formatValue) {
                return formatValue(val)
            }
            return unit ? `${val} ${unit}` : String(val)
        },
        [formatValue, unit]
    )

    const rangeSpan = max - min
    const minRatio = rangeSpan ? (localMin - min) / rangeSpan : 0
    const maxRatio = rangeSpan ? (localMax - min) / rangeSpan : 1

    const draggingRef = useRef<'min' | 'max' | null>(null)

    const valueFromX = useCallback(
        (clientX: number) => {
            const el = trackRef.current
            if (!el) return min
            const rect = el.getBoundingClientRect()
            const x = clientX - rect.left
            const ratio = Math.max(0, Math.min(1, x / rect.width))
            const raw = min + ratio * rangeSpan
            const stepped = step ? Math.round(raw / step) * step : raw
            return Math.max(min, Math.min(max, stepped))
        },
        [min, max, rangeSpan, step]
    )

    const handleTrackPointerDown = useCallback(
        (e: React.PointerEvent) => {
            if (e.button !== 0) return
            const el = trackRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const x = e.clientX - rect.left
            const ratio = Math.max(0, Math.min(1, x / rect.width))
            const distToMin = Math.abs(ratio - minRatio)
            const distToMax = Math.abs(ratio - maxRatio)
            const which: 'min' | 'max' = distToMin <= distToMax ? 'min' : 'max'
            const value = valueFromX(e.clientX)
            e.preventDefault()
            e.stopPropagation()
            draggingRef.current = which
            if (which === 'min') {
                handleMinChange(Math.min(value, localMax))
            } else {
                handleMaxChange(Math.max(value, localMin))
            }
            el.setPointerCapture(e.pointerId)
        },
        [minRatio, maxRatio, valueFromX, handleMinChange, handleMaxChange, localMin, localMax]
    )

    const handleTrackPointerMove = useCallback(
        (e: React.PointerEvent) => {
            const which = draggingRef.current
            if (!which) return
            e.preventDefault()
            const value = valueFromX(e.clientX)
            if (which === 'min') {
                handleMinChange(Math.min(value, localMax))
            } else {
                handleMaxChange(Math.max(value, localMin))
            }
        },
        [valueFromX, handleMinChange, handleMaxChange, localMin, localMax]
    )

    const handleTrackPointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (draggingRef.current) {
                e.currentTarget.releasePointerCapture?.(e.pointerId)
                draggingRef.current = null
            }
        },
        []
    )

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Label - eğer label varsa göster */}
            {label && (
                <label className="block text-sm font-medium text-alsero-dark-gray mb-2">
                    {label}
                </label>
            )}
            
            {/* Range Slider - track tıklanınca hangi thumb'a yakınsa o güncellenir (sol/sağ yuvarlaklar tıklanabilir) */}
            <div
                ref={trackRef}
                className="relative h-2 bg-gray-200 rounded-lg select-none"
                onPointerDownCapture={handleTrackPointerDown}
                onPointerMove={handleTrackPointerMove}
                onPointerUp={handleTrackPointerUp}
                onPointerLeave={handleTrackPointerUp}
                onPointerCancel={handleTrackPointerUp}
            >
                <div 
                    className="absolute h-2 bg-alsero-first rounded-lg pointer-events-none"
                    style={{
                        left: `${minRatio * 100}%`,
                        width: `${(maxRatio - minRatio) * 100}%`,
                    }}
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={localMin}
                    onChange={(e) => handleMinChange(Number(e.target.value))}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer slider-thumb"
                    style={{ zIndex: 1 }}
                    aria-label={label ? `${label} minimum` : undefined}
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={localMax}
                    onChange={(e) => handleMaxChange(Number(e.target.value))}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer slider-thumb"
                    style={{ zIndex: 2 }}
                    aria-label={label ? `${label} maximum` : undefined}
                />
            </div>

            {/* Price Display */}
            <div className="flex items-center justify-between pt-2">
                <span className="text-sm font-medium text-alsero-dark-gray">
                    {formatDisplayValue(localMin)} - {formatDisplayValue(localMax)}
                </span>
            </div>
        </div>
    )
}
